import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, ArrowBack as BackIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CATEGORIA_SAIDA_LABEL } from '../../types';
import recurringCashMovementService, { RecurringCashMovement } from '../../services/recurringCashMovementService';

const statusColor = (s: RecurringCashMovement['status']) => {
  if (s === 'active') return 'success';
  if (s === 'paused') return 'warning';
  return 'default';
};

const statusLabel = (s: RecurringCashMovement['status']) => {
  if (s === 'active') return 'Ativa';
  if (s === 'paused') return 'Pausada';
  return 'Finalizada';
};

const RecurringCashMovementList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<RecurringCashMovement[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await recurringCashMovementService.getAll();
      setItems(data);
    } catch (e) {
      console.error(e);
      setError('Não foi possível carregar as recorrências de movimentações.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Excluir esta recorrência? Isso não apaga movimentações já geradas.')) return;
    try {
      await recurringCashMovementService.delete(id);
      await load();
    } catch (e) {
      console.error(e);
      setError('Não foi possível excluir.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/movimentacoes')} color="inherit">
          Voltar
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Recorrências (movimentações)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/movimentacoes/recorrentes/novo')}
        >
          Nova recorrência
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cadastre boletos/mensalidades/parcelas para gerar contas a pagar automaticamente (saídas pendentes) nas
        movimentações.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Status</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Início</TableCell>
                <TableCell>Fim</TableCell>
                <TableCell>Parcelas</TableCell>
                <TableCell align="center" width={90}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nenhuma recorrência cadastrada.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Chip size="small" label={statusLabel(r.status)} color={statusColor(r.status) as any} variant="outlined" />
                    </TableCell>
                    <TableCell>{CATEGORIA_SAIDA_LABEL[r.category] ?? r.category}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell align="right">
                      {Number(r.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                    <TableCell>
                      {r.start_date ? format(parseISO(r.start_date), 'dd/MM/yyyy', { locale: ptBR }) : '—'}
                    </TableCell>
                    <TableCell>
                      {r.end_date ? format(parseISO(r.end_date), 'dd/MM/yyyy', { locale: ptBR }) : '—'}
                    </TableCell>
                    <TableCell>{r.installments_total ? `1/${r.installments_total}…` : '—'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/movimentacoes/recorrentes/editar/${r.id}`)}
                        aria-label="Editar"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => void handleDelete(r.id)} aria-label="Excluir">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default RecurringCashMovementList;

